import { ArrowBack, InfoOutlined } from "@mui/icons-material";
import { Autocomplete, Box, Button, Card, CardContent, CircularProgress, Divider, IconButton, MenuItem, Tab, Tabs, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import locales from "../../../constants/locales";
import Loading from "../../../components/loading/loading";
import HTMLEditor from "../../../components/html_editor/html_editor";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import { black, greyEnabled, orange, orangeLightActive, purple } from "../../../theme/colors";
import { cms_news } from "../../../routes/routes_path";
import { createNews, getMediaById, getNewsById, getNewsSourceList, updateNews } from "../../../services/cms/cms";
import MediaPicker from "../components/media_picker/media_picker";
import TargetingFields, { buildTargetingPayload, defaultTargeting, targetingFromResponse, TargetingFormValues } from "../components/targeting_fields/targeting_fields";
import VideoPicker from "../components/video_picker/video_picker";
import { CmsStatus, Media, News, NewsSource } from "../models/cms";

interface NewsFormValues {
  title: string;
  content: string;
  status: CmsStatus;
  news_link: string;
  video_url: string;
  active_date: string | null;
  expire_date: string | null;
  featured_image: Media | null;
  news_source_id: number | null;
  publishedAt: string;
  targeting: TargetingFormValues;
}

const defaultValues: NewsFormValues = {
  title: "",
  content: "",
  status: "draft",
  news_link: "",
  video_url: "",
  active_date: null,
  expire_date: null,
  featured_image: null,
  news_source_id: null,
  publishedAt: "",
  targeting: defaultTargeting,
};

const NEWS_MEDIA_ASPECT_RATIO = "16:9";
const NEWS_MEDIA_SIZE_LABEL = "1920 x 1080 px";

function slugify(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// A small bold, uppercase divider label — breaks the form into scannable
// groups instead of one long flat list of fields.
function SectionHeader({ children, mt = 4 }: { children: ReactNode; mt?: number }) {
  return (
    <Box mt={mt} mb={1.5}>
      <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function AddEditNews({ viewMode = false }: { viewMode?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useFeedback();
  const isEdit = !!id;
  // News is always created in English; other languages are added afterwards
  // from the edit/view screen, same convention as src/views/schemes/.
  const [selectedLocale, setSelectedLocale] = useState("en");
  const { control, handleSubmit, watch, setValue, reset } = useForm<NewsFormValues>({
    defaultValues: { ...defaultValues, publishedAt: dayjs().toISOString() },
  });
  const { selectedOrgId } = useSelectedOrgStore();

  const { data: newsDetails, isLoading: isNewsLoading } = useQuery<News | null>(
    ["cms-news-details", id, selectedLocale],
    async () => {
      const response = await getNewsById(id!, selectedLocale);
      return response?.data?.data;
    },
    { enabled: !!id }
  );

  const { data: newsSourceOptions } = useQuery(
    ["cms-news-sources", selectedLocale],
    async () => {
      const response = await getNewsSourceList({ isActive: true, locale: selectedLocale });
      return (response?.data?.data?.list || []) as NewsSource[];
    }
  );

  // getNewsById only returns the raw *_id integers, not a nested media
  // object, so the picker's existing-image preview needs a separate fetch.
  const { data: featuredImageMedia } = useQuery(
    ["cms-media", newsDetails?.featured_image_id],
    async () => {
      const response = await getMediaById(newsDetails!.featured_image_id!);
      return response?.data?.data as Media;
    },
    { enabled: !!newsDetails?.featured_image_id }
  );

  useEffect(() => {
    if (newsDetails) {
      reset({
        title: newsDetails.title || "",
        content: newsDetails.content || "",
        status: newsDetails.status || "draft",
        news_link: newsDetails.news_link || "",
        video_url: newsDetails.video_url || "",
        active_date: newsDetails.active_date || null,
        expire_date: newsDetails.expire_date || null,
        featured_image: null,
        news_source_id: newsDetails.news_source_id ?? null,
        publishedAt: newsDetails.published_at || dayjs().toISOString(),
        targeting: targetingFromResponse(newsDetails.targeting),
      });
    }
  }, [newsDetails, reset]);

  useEffect(() => {
    if (featuredImageMedia) setValue("featured_image", featuredImageMedia);
  }, [featuredImageMedia, setValue]);

  const title = watch("title");
  const featuredImage = watch("featured_image");
  const newsSourceId = watch("news_source_id");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = (values: NewsFormValues) => {
    setIsSubmitting(true);
    const payload = {
      title: values.title,
      slug: slugify(values.title),
      excerpt: "",
      content: values.content || "",
      status: values.status,
      news_link: values.news_link || null,
      video_url: values.video_url || null,
      is_active: true,
      active_date: values.active_date || null,
      expire_date: values.expire_date || null,
      published_at: values.publishedAt || dayjs().toISOString(),
      // The backend requires both featured_image_id and featured_image_web_id;
      // the form only exposes a single image picker, so the same image is
      // used for both.
      featured_image_id: values.featured_image?.id,
      featured_image_web_id: values.featured_image?.id,
      news_source_id: values.news_source_id || null,
      targeting: buildTargetingPayload(values.targeting),
      ...(isEdit ? {} : { orgId: selectedOrgId || undefined }),
    };

    const request = isEdit ? updateNews(id!, selectedLocale, payload) : createNews(payload);
    request
      .then(() => {
        showSnackbar(isEdit ? "News updated successfully" : "News added successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-news"] });
        // The list query above doesn't cover this item's own details cache
        // (keyed separately by id+locale) - without this, re-opening Edit
        // within the 5-minute default staleTime serves the stale
        // pre-update data until a hard refresh.
        queryClient.invalidateQueries({ queryKey: ["cms-news-details", id] });
        navigate(cms_news);
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <MainLayout>
      <Card sx={{ width: "100%", borderRadius: 4, m: 1 }} elevation={1}>
        <CardContent>
          <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight="600" ml={1}>
              {viewMode ? "View News" : isEdit ? "Edit News" : "Add News"}
            </Typography>
          </Box>
          <Divider sx={{ my: 3, opacity: 0.5, borderBottom: "1px dotted #7C797C" }} />

          {isEdit && isNewsLoading ? (
            <Loading label="Loading news..." />
          ) : (
          <>
          {!isEdit && !viewMode && (
            <Box maxWidth={360}>
              <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
            </Box>
          )}

          {isEdit && (
            <>
              <Tabs
                value={locales.findIndex((locale) => locale.code === selectedLocale)}
                onChange={(_, index: number) => setSelectedLocale(locales[index].code)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { backgroundColor: purple, height: 3, borderRadius: 3 } }}
                sx={{ mt: 2 }}
              >
                {locales.map((locale) => (
                  <Tab
                    key={locale.code}
                    label={locale.name}
                    sx={{ textTransform: "capitalize", minWidth: 90, color: greyEnabled, "&.Mui-selected": { color: purple } }}
                  />
                ))}
              </Tabs>
              <Divider />

              {newsDetails?.is_translated === false && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: orangeLightActive, border: `1px solid ${orangeLightActive}` }}
                >
                  <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                  <Typography variant="body2" color={black}>
                    This news item has no content in{" "}
                    {locales.find((locale) => locale.code === selectedLocale)?.name || selectedLocale} yet — fill in the
                    details below to add this translation.
                  </Typography>
                </Box>
              )}
            </>
          )}

          <SectionHeader mt={!isEdit && !viewMode ? 3 : 2}>Basic Details</SectionHeader>

          <Controller
            name="title"
            control={control}
            rules={{
              required: "Title is required",
              minLength: { value: 3, message: "Title must be at least 3 characters" },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                required
                label={<>Title <span style={{ color: "red" }}>*</span></>}
                margin="normal"
                disabled={viewMode}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Box display="flex" flexDirection="row" gap={4}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select fullWidth label="Status" margin="normal" disabled={viewMode}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="news_source_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  fullWidth
                  disabled={viewMode}
                  options={newsSourceOptions || []}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  value={newsSourceOptions?.find((option) => option.id === field.value) || null}
                  onChange={(_, newValue) => field.onChange(newValue?.id ?? null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={!isEdit}
                      label={isEdit ? "Source" : <>Source <span style={{ color: "red" }}>*</span></>}
                      margin="normal"
                    />
                  )}
                />
              )}
            />
          </Box>

          <SectionHeader>Content</SectionHeader>

          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <HTMLEditor value={field.value} onChange={field.onChange} placeholder="Enter news content" readOnly={viewMode} />
            )}
          />

          <SectionHeader>Media</SectionHeader>

          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={4}>
            <Controller
              name="featured_image"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  label="Featured Image"
                  required
                  value={field.value}
                  disabled={viewMode}
                  onChange={field.onChange}
                  recommendedAspectRatio={NEWS_MEDIA_ASPECT_RATIO}
                  recommendedSizeLabel={NEWS_MEDIA_SIZE_LABEL}
                />
              )}
            />
          </Box>

          <Box mt={3}>
            <Controller
              name="video_url"
              control={control}
              render={({ field }) => (
                <VideoPicker
                  label="Video"
                  value={field.value}
                  disabled={viewMode}
                  onChange={field.onChange}
                  recommendedAspectRatio={NEWS_MEDIA_ASPECT_RATIO}
                  recommendedSizeLabel={NEWS_MEDIA_SIZE_LABEL}
                />
              )}
            />
          </Box>

          <SectionHeader>Link & Scheduling</SectionHeader>

          <Controller
            name="news_link"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="News Link (URL)" margin="normal" disabled={viewMode} />
            )}
          />

          <Box display="flex" flexDirection="row" gap={4} mt={2}>
            <Controller
              name="active_date"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Active From"
                  readOnly={viewMode}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value: Dayjs | null) => field.onChange(value ? value.toISOString() : null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
            <Controller
              name="expire_date"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Expires On"
                  readOnly={viewMode}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value: Dayjs | null) => field.onChange(value ? value.toISOString() : null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Box>

          <SectionHeader>Audience</SectionHeader>

          <TargetingFields control={control} setValue={setValue} viewMode={viewMode} />

          <Box display="flex" flexDirection="row" justifyContent="space-between" mt={5}>
            <Button onClick={() => navigate(-1)} variant="outlined" sx={{ color: black }} disabled={isSubmitting}>
              {viewMode ? "Back" : "Cancel"}
            </Button>
            {!viewMode && (
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !title || title.trim().length < 3 || !featuredImage || (!isEdit && !newsSourceId)}
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
              >
                {isSubmitting ? (isEdit ? "Updating..." : "Adding...") : isEdit ? "Update News" : "Add News"}
              </Button>
            )}
          </Box>
          </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
}
